import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import styles from "./Step2.module.css";
import { getRetellVoices } from "../../Store/apiStore";
import PopUp from "../Popup/Popup";
import useOnboardingTracker from "../../hooks/useOnboardingTracker";
import decodeToken from "../../lib/decodeToken";

const Step2 = forwardRef(
  (
    {
      onNext,
      onBack,
      onValidationError,
      onSuccess,
      onFailed,
      setLoading,
      onStepChange,
    },
    ref
  ) => {
    const cap = (g) =>
      g ? g.charAt(0).toUpperCase() + g.slice(1).toLowerCase() : "";

    const [selectedGender, setSelectedGender] = useState(() => {
      const saved =
        sessionStorage.getItem("agentGender") ||
        localStorage.getItem("agentGender");
      return saved ? cap(saved) : "Female";
    });

    const [selectedVoice, setSelectedVoice] = useState(null);
    const [listVoices, setListVoices] = useState([]);
    const [filteredVoices, setFilteredVoices] = useState([]);

    const audioRefs = useRef([]);
    const [playingIdx, setPlayingIdx] = useState(null);

    const [showPopup, setShowPopup] = useState(false);
    const [popupType, setPopupType] = useState(null);
    const [popupMessage, setPopupMessage] = useState("");

    const { trackStep } = useOnboardingTracker();

    const [hydrated, setHydrated] = useState(false);
    const prevGenderRef = useRef(null);

    // 1) Fetch voices
    useEffect(() => {
      const fetchRetellVoiceList = async () => {
        try {
          const voiceResponses = await getRetellVoices();
          setListVoices(voiceResponses.data || []);
        } catch (error) {
          console.error("Error fetching voices:", error);
        }
      };
      fetchRetellVoiceList();
    }, []);

    // 2) Hydrate gender + voice from storage (runs once after voices are loaded)
    useEffect(() => {
      if (!listVoices.length) return;

      const storedGender =
        sessionStorage.getItem("agentGender") ||
        localStorage.getItem("agentGender");
      const storedVoiceId =
        sessionStorage.getItem("agentVoice") ||
        localStorage.getItem("agentVoice");

      if (storedGender) setSelectedGender(cap(storedGender));

      if (storedVoiceId) {
        const v = listVoices.find((x) => x.voice_id === storedVoiceId);
        if (v) setSelectedVoice(v);
      }

      setHydrated(true);
    }, [listVoices]);

    // 3) Filter voices by gender
    useEffect(() => {
      if (!listVoices?.length || !selectedGender) return;

      const filtered = listVoices.filter(
        (voice) =>
          voice.provider === "elevenlabs" &&
          voice.gender === selectedGender.toLowerCase()
      );

      setFilteredVoices(filtered);
      sessionStorage.setItem("agentGender", selectedGender);
    }, [selectedGender, listVoices]);

    // 4) Reset voice ONLY when gender actually changes (not on back navigation)
    useEffect(() => {
      if (!hydrated) return;

      if (prevGenderRef.current == null) {
        prevGenderRef.current = selectedGender;
        return;
      }

      if (prevGenderRef.current !== selectedGender) {
        // reset selection
        setSelectedVoice(null);

        // stop audio
        setPlayingIdx(null);
        audioRefs.current.forEach((a) => {
          if (a) {
            a.pause();
            a.currentTime = 0;
          }
        });

        // clear stored voice
        sessionStorage.removeItem("agentVoice");
        sessionStorage.removeItem("agentVoiceAccent");
        sessionStorage.removeItem("VoiceAgentName");
      }

      prevGenderRef.current = selectedGender;
    }, [selectedGender, hydrated]);

    // 5) Persist selected voice to sessionStorage
    useEffect(() => {
      if (
        selectedVoice &&
        selectedVoice.voice_id &&
        selectedVoice.voice_name &&
        selectedVoice.accent
      ) {
        sessionStorage.setItem("agentVoice", selectedVoice.voice_id);
        sessionStorage.setItem("agentVoiceAccent", selectedVoice.accent);
        sessionStorage.setItem("VoiceAgentName", selectedVoice.voice_name);
        sessionStorage.setItem(
          "agentName",
          selectedVoice.voice_name.replace(/\s*\(.*?\)/, "")
        );
      }
    }, [selectedVoice]);

    // Same as the edit-voice screen (EditGender/Step2): reload a preview whose
    // first load failed, and swallow the AbortError a second tap causes.
    const startPlayback = (audio) => {
      if (audio.error || audio.networkState === audio.NETWORK_NO_SOURCE) audio.load();
      const started = audio.play();
      if (!started || typeof started.catch !== "function") return;
      started.catch((err) => {
        if (err?.name !== "AbortError") {
          console.warn("Voice preview could not play:", err);
        }
      });
    };

    const togglePlay = (idx) => {
      const thisAudio = audioRefs.current[idx];
      if (!thisAudio) return;

      if (playingIdx === idx) {
        thisAudio.pause();
        setPlayingIdx(null);
        return;
      }

      if (playingIdx !== null) {
        const playingAudio = audioRefs.current[playingIdx];
        playingAudio?.pause();
        if (playingAudio) playingAudio.currentTime = 0;
      }

      startPlayback(thisAudio);
      setPlayingIdx(idx);

      thisAudio.onended = () => setPlayingIdx(null);
    };

    const playAudio = (idx) => {
      const selectedAudio = audioRefs.current[idx];
      if (!selectedAudio) return;

      if (playingIdx !== null && playingIdx !== idx) {
        const currentAudio = audioRefs.current[playingIdx];
        currentAudio?.pause();
        if (currentAudio) currentAudio.currentTime = 0;
      }

      startPlayback(selectedAudio);
      setPlayingIdx(idx);

      selectedAudio.onended = () => setPlayingIdx(null);
    };

    useImperativeHandle(ref, () => ({
      validate: () => {
        if (!selectedGender) {
          onValidationError?.({
            type: "failed",
            message: "Please select a gender!",
          });
          return false;
        }

        if (!selectedVoice || !selectedVoice.voice_id) {
          onValidationError?.({
            type: "failed",
            message: "Please select a voice!",
          });
          return false;
        }

        onStepChange?.(7);

        trackStep({
          stepName: "select_voice",
          stepNo: 6,
          screenName: "Select Voice",
          email: localStorage.getItem("userEmail"),
          userId: decodeToken(localStorage.getItem("token"))?.id || null,
          data: { gender: selectedGender, voice: selectedVoice },
        });

        return true;
      },
    }));

    return (
      <>
        <div className={styles.container}>
          <div className={styles.genderContainer}>
            {["Female", "Male"].map((gender) => (
              <label
                key={gender}
                className={`${styles.genderCard} ${
                  selectedGender === gender ? styles.active : ""
                }`}
              >
                <span className={styles.icon}>
                  {gender === "Female" ? (
                    <img src="svg/female-icon.svg" alt="Female Icon" />
                  ) : (
                    <img src="svg/male-icon.svg" alt="Male Icon" />
                  )}
                </span>
                <span>{gender}</span>
                <input
                  type="radio"
                  name="gender"
                  value={gender}
                  checked={selectedGender === gender}
                  onChange={() => setSelectedGender(gender)}
                  className={styles.radioInput}
                />
              </label>
            ))}
          </div>

          {selectedGender && (
            <h2 className={styles.sectionTitle}>Select Voice</h2>
          )}

          <div className={styles.voiceGrid}>
            {filteredVoices.map((voice, idx) => (
              <label
                key={voice.voice_id ?? idx}
                className={`${styles.voiceCard} ${
                  selectedVoice?.voice_id === voice.voice_id
                    ? styles.active
                    : ""
                }`}
              >
                <input
                  type="radio"
                  name="voice"
                  value={voice.voice_id}
                  checked={selectedVoice?.voice_id === voice.voice_id}
                  onChange={() => {
                    setSelectedVoice(voice);
                    playAudio(idx);
                  }}
                  className={styles.radioInput}
                />

                <div
                  className={styles.playIcon}
                  onClick={(e) => {
                    e.preventDefault();
                    togglePlay(idx);
                    setSelectedVoice(voice);
                  }}
                  title="Preview"
                >
                  <img
                    src={
                      playingIdx === idx
                        ? "svg/Pause-icon.svg"
                        : "svg/Play-icon.svg"
                    }
                    alt={playingIdx === idx ? "Pause" : "Play"}
                  />
                </div>

                {/* preload="none": load a preview only when it is played (see
                    EditGender/Step2). No `type`: Sarvam previews are wav, and a
                    declared mp3 type made the browser skip them silently. */}
                <audio
                  ref={(el) => (audioRefs.current[idx] = el)}
                  style={{ display: "none" }}
                  preload="none"
                >
                  <source src={voice.preview_audio_url} />
                </audio>

                <div>
                  <p className={styles.voiceName}>
                    {voice.voice_name?.replace(/\s*\(.*?\)/, "")}
                  </p>
                  <p className={styles.voiceDesc}>{voice.accent} Accent</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {showPopup && (
          <PopUp
            type={popupType}
            onClose={() => setShowPopup(false)}
            message={popupMessage}
          />
        )}
      </>
    );
  }
);

export default Step2;
